import type { CSSProperties } from 'react';

// Each palette pairs a dark foreground with a light surface. Never put yellow text on white.
export const FIDELITY_TIER_COLORS = {
  BROWN: {
    label: 'Marrone',
    accent: '#854d0e',
    surface: '#fef3c7',
    ink: '#422006',
    swatch: '#a16207',
  },
  BRONZE: {
    label: 'Bronzo',
    accent: '#9a3412',
    surface: '#ffedd5',
    ink: '#431407',
    swatch: '#c2410c',
  },
  SILVER: {
    label: 'Argento',
    accent: '#475569',
    surface: '#e2e8f0',
    ink: '#0f172a',
    swatch: '#64748b',
  },
  GOLD: {
    label: 'Oro / Giallo',
    accent: '#854d0e',
    surface: '#fef08a',
    ink: '#422006',
    swatch: '#eab308',
  },
  GREEN: {
    label: 'Verde',
    accent: '#166534',
    surface: '#dcfce7',
    ink: '#052e16',
    swatch: '#15803d',
  },
  BLUE: {
    label: 'Blu',
    accent: '#1e40af',
    surface: '#dbeafe',
    ink: '#172554',
    swatch: '#2563eb',
  },
  PURPLE: {
    label: 'Viola',
    accent: '#6b21a8',
    surface: '#f3e8ff',
    ink: '#3b0764',
    swatch: '#9333ea',
  },
} as const;
export type FidelityTierColor =
  keyof typeof FIDELITY_TIER_COLORS | `#${string}`;

/** Persist the selected swatch, not the darker accent used for readable UI text. */
export function normalizeFidelityTierColor(color: string): `#${string}` {
  const value = color.trim();
  const palette = Object.entries(FIDELITY_TIER_COLORS).find(
    ([key]) => key === value.toUpperCase(),
  )?.[1];
  const hex = palette?.swatch ?? value;
  if (!/^#[0-9a-f]{6}$/i.test(hex)) throw Error('Scegli un colore valido.');
  return hex.toUpperCase() as `#${string}`;
}

export function resolveFidelityTierColor(color: string) {
  const legacy = Object.entries(FIDELITY_TIER_COLORS).find(
    ([key]) => key === color,
  )?.[1];
  if (legacy) return legacy;
  if (!/^#[0-9a-f]{6}$/i.test(color)) return FIDELITY_TIER_COLORS.BLUE;
  const channels = [1, 3, 5].map((i) => parseInt(color.slice(i, i + 2), 16));
  const mix = (white: number) =>
    '#' +
    channels
      .map((c) =>
        Math.round(c * (1 - white) + 255 * white)
          .toString(16)
          .padStart(2, '0'),
      )
      .join('');
  return {
    label: color,
    accent: color,
    swatch: color,
    surface: mix(0.88),
    ink:
      '#' +
      channels
        .map((c) =>
          Math.round(c * 0.3)
            .toString(16)
            .padStart(2, '0'),
        )
        .join(''),
  };
}
export interface FidelityTier {
  id: string;
  threshold: number;
  name: string;
  color: FidelityTierColor;
  discountPercent: number;
  tagsApplied?: string;
}
export const sortFidelityTiers = (tiers: FidelityTier[]) =>
  [...tiers].sort((a, b) => a.threshold - b.threshold);
export function currentFidelityTier(
  tiers: FidelityTier[],
  points: number,
): FidelityTier | null {
  return (
    sortFidelityTiers(tiers)
      .filter((tier) => tier.threshold <= points)
      .at(-1) ?? null
  );
}
export function validateFidelityTiers(tiers: FidelityTier[]): void {
  const ids = new Set<string>(),
    thresholds = new Set<number>();
  const fail = (tier: FidelityTier, field: string, message: string): never => {
    throw Object.assign(
      Error(`Livello «${tier.name.trim() || 'Nuovo livello'}»: ${message}`),
      {
        fidelityStep: 3,
        entityId: tier.id,
        field,
      },
    );
  };
  for (const tier of tiers) {
    if (!tier.id || ids.has(tier.id))
      fail(tier, 'id', 'Ogni livello deve avere un identificativo univoco.');
    if (!tier.name.trim())
      fail(tier, 'name', 'Inserisci il nome di ogni livello Fidelity.');
    if (!Number.isSafeInteger(tier.threshold) || tier.threshold < 0)
      fail(
        tier,
        'threshold',
        'Le soglie cumulative devono essere punti interi, da 0 in su.',
      );
    if (thresholds.has(tier.threshold))
      fail(
        tier,
        'threshold',
        'Configura un solo livello per ogni soglia cumulativa.',
      );
    if (
      !(tier.color in FIDELITY_TIER_COLORS) &&
      !/^#[0-9a-f]{6}$/i.test(tier.color)
    )
      fail(tier, 'color', 'Scegli un colore valido.');
    if (
      !Number.isFinite(tier.discountPercent) ||
      tier.discountPercent < 0 ||
      tier.discountPercent > 100
    )
      fail(
        tier,
        'discountPercent',
        'Lo sconto del livello deve essere compreso tra 0 e 100%.',
      );
    ids.add(tier.id);
    thresholds.add(tier.threshold);
  }
  const sorted = sortFidelityTiers(tiers);
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].discountPercent < sorted[i - 1].discountPercent)
      fail(
        sorted[i],
        'discountPercent',
        `Lo sconto di ${sorted[i].name} deve essere almeno ${String(sorted[i - 1].discountPercent)}%, come il livello ${sorted[i - 1].name}. Lo sconto non può diminuire aumentando i punti.`,
      );
  }
}
export function tierStyle(
  tier: FidelityTier | null | undefined,
): CSSProperties {
  if (!tier) return {};
  const palette = resolveFidelityTierColor(tier.color);
  const rgb = (hex: string) =>
    [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)).join(' ');
  return {
    '--tier-button-ink':
      [1, 3, 5]
        .map((i) => parseInt(palette.accent.slice(i, i + 2), 16))
        .reduce((sum, c, i) => sum + c * [0.299, 0.587, 0.114][i], 0) > 160
        ? '#172033'
        : '#ffffff',
    '--tier-accent-rgb': rgb(palette.accent),
    '--tier-surface-rgb': rgb(palette.surface),
    '--tier-accent': palette.accent,
    '--tier-surface': palette.surface,
    '--tier-ink': palette.ink,
    '--tier-swatch': palette.swatch,
  } as CSSProperties;
}
